import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/apiClient";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const SLUG_ORDER = ["privacy", "terms", "refund", "disclaimer"];
const SLUG_LABEL = {
  privacy: { id: "Kebijakan Privasi", en: "Privacy Policy" },
  terms: { id: "Syarat & Ketentuan", en: "Terms & Conditions" },
  refund: { id: "Kebijakan Refund", en: "Refund Policy" },
  disclaimer: { id: "Disclaimer", en: "Disclaimer" },
};

export default function AdminLegalPages() {
  const { lang } = useLang();
  const [pages, setPages] = useState({});
  const [activeSlug, setActiveSlug] = useState("privacy");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/legal").then(r => {
      const map = {};
      (r.data || []).forEach(p => { map[p.slug] = p; });
      setPages(map);
    });
  }, []);

  const cur = pages[activeSlug];
  const setCur = (patch) => setPages(prev => ({ ...prev, [activeSlug]: { ...prev[activeSlug], ...patch } }));

  const save = async () => {
    if (!cur) return;
    setSaving(true);
    try {
      const body = { title_id: cur.title_id, title_en: cur.title_en, body_id: cur.body_id, body_en: cur.body_en };
      const { data } = await api.put(`/admin/legal/${activeSlug}`, body);
      setPages(prev => ({ ...prev, [activeSlug]: data }));
      toast.success(lang === "id" ? "Halaman disimpan" : "Page saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <AdminLayout>
      <h1 className="font-display text-3xl tracking-tight mb-2">{lang === "id" ? "Halaman Legal" : "Legal Pages"}</h1>
      <p className="text-sm text-muted-foreground mb-6">
        {lang === "id"
          ? "Edit isi halaman Kebijakan Privasi, Syarat & Ketentuan, Kebijakan Refund, dan Disclaimer. Konten menerima HTML dasar (h3, p, ul, li, strong)."
          : "Edit content for Privacy Policy, Terms & Conditions, Refund Policy, and Disclaimer. Content accepts basic HTML (h3, p, ul, li, strong)."}
      </p>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="legal-tabs">
        {SLUG_ORDER.map(s => (
          <button
            key={s}
            onClick={() => setActiveSlug(s)}
            className={`px-4 py-2 rounded-full border text-sm transition ${activeSlug === s ? "bg-terracotta-500 text-cream-50 border-terracotta-500" : "bg-card border-border hover:bg-secondary"}`}
            data-testid={`legal-tab-${s}`}
          >
            {SLUG_LABEL[s][lang] || SLUG_LABEL[s].id}
          </button>
        ))}
      </div>

      {cur ? (
        <div className="max-w-4xl space-y-5 p-6 rounded-2xl border border-border bg-card" data-testid={`legal-editor-${activeSlug}`}>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Judul (ID)" : "Title (ID)"}</Label>
              <Input value={cur.title_id || ""} onChange={(e) => setCur({ title_id: e.target.value })} className="h-10" data-testid={`legal-${activeSlug}-title-id`} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Judul (EN)" : "Title (EN)"}</Label>
              <Input value={cur.title_en || ""} onChange={(e) => setCur({ title_en: e.target.value })} className="h-10" data-testid={`legal-${activeSlug}-title-en`} />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Isi (ID) — HTML" : "Body (ID) — HTML"}</Label>
            <Textarea rows={12} value={cur.body_id || ""} onChange={(e) => setCur({ body_id: e.target.value })} className="font-mono text-xs" data-testid={`legal-${activeSlug}-body-id`} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Isi (EN) — HTML" : "Body (EN) — HTML"}</Label>
            <Textarea rows={12} value={cur.body_en || ""} onChange={(e) => setCur({ body_en: e.target.value })} className="font-mono text-xs" data-testid={`legal-${activeSlug}-body-en`} />
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={saving} className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50" data-testid={`legal-${activeSlug}-save-btn`}>
              {saving ? (lang === "id" ? "Menyimpan…" : "Saving…") : (lang === "id" ? "Simpan" : "Save")}
            </Button>
            <a href={`/legal/${activeSlug}`} target="_blank" rel="noreferrer" className="text-sm text-terracotta-600 hover:underline" data-testid={`legal-${activeSlug}-preview-link`}>
              {lang === "id" ? "Buka halaman publik →" : "Open public page →"}
            </a>
          </div>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">Loading…</div>
      )}
    </AdminLayout>
  );
}
